import { useContext } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useGetTableScheduleDetails } from "src/data/restaurant/queries/restaurant";
import { CircularLoader } from "src/design/CircularLoader";
import { NoAccess } from "src/design/NoAccess";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { HEADER_HEIGHT } from "src/components/Main/Pages/Home/PageRestau";
import { TableSetupPrompt } from "./components/TableSetupPrompt";
import { TableScheduleBoard } from "./components/TableScheduleBoard";

// The board fills the viewport below the app bar, minus the padding the page
// shell adds, so all 48 rows of the day fit without the page ever scrolling.
const boardHeight = (shellPadding: number): string =>
  `calc(100vh - ${HEADER_HEIGHT + shellPadding * 2}px)`;

const TableSchedulePage = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const resolvedId = restaurantId ?? "";

  const { restaurantDetails, getRestaurantDetails, refreshing, error } =
    useGetTableScheduleDetails(resolvedId);

  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);
  const restaurant = restaurants.find((entry) => entry.id === resolvedId);
  const restaurantCapabilities = restaurant?.capabilities.map(
    (capability) => capability.name,
  );

  const canUpdate = isFeatureSectionEnabled(
    ["table_schedule_update"],
    "table_scheduling",
    user,
    restaurantCapabilities,
  );
  const canCreate = isFeatureSectionEnabled(
    ["booking_creation"],
    "booking_management",
    user,
    restaurantCapabilities,
  );

  // Both gates turn on `restaurantDetails` rather than on `loading`/`error`
  // alone, so they only ever fire on the very first read. Every later read is a
  // refresh of a board that is already on screen — adding or removing a table
  // triggers one — and swapping that board for a spinner would unmount it,
  // dropping the day the user had navigated to back to today. A refresh that
  // fails leaves the last good board up; the action that triggered it reports
  // its own failure.
  if (error && !restaurantDetails) {
    return <NoAccess />;
  }

  if (!restaurantDetails) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 8, width: "100%" }}
      >
        <CircularLoader />
      </Box>
    );
  }

  if (restaurantDetails.numberOfTables === null) {
    return (
      <TableSetupPrompt
        restaurantId={resolvedId}
        canUpdate={canUpdate}
        onSuccess={getRestaurantDetails}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 0,
        height: {
          xs: boardHeight(8),
          sm: boardHeight(16),
          md: boardHeight(32),
        },
      }}
    >
      <TableScheduleBoard
        restaurantId={resolvedId}
        numberOfTables={restaurantDetails.numberOfTables}
        bookingDuration={restaurantDetails.bookingDuration}
        schedule={restaurantDetails.schedule}
        leaves={restaurantDetails.leaves}
        canUpdate={canUpdate}
        canCreate={canCreate}
        onNumberOfTablesChange={getRestaurantDetails}
        isRefreshingDetails={refreshing}
      />
    </Box>
  );
};

export default TableSchedulePage;
